import React, { useState, useEffect } from 'react';
import { IonContent, IonButton, IonIcon, IonAlert, IonInput, IonPage } from '@ionic/react';
import { _defaultButtonColor, _dict } from './constants';
import { copyOutline } from 'ionicons/icons';
import { useGlobalStore } from './stateManagement/globalZustand';

interface BrowserWarningState {
  showAlert: boolean;
  currentOptionIndex: number;
  options: Array<{ message: string; action?: () => void }>;
}

const BrowserWarning: React.FC = () => {
  const [state, setState] = useState<BrowserWarningState>({
    showAlert: false,
    currentOptionIndex: 0,
    options: [],
  });

  const { lang } = useGlobalStore();
  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const isFacebookBrowser = /FBAN|FBAV/i.test(userAgent);
    const isAndroid = /android/i.test(userAgent);

    const options: { message: string; action?: (() => void) | (() => Window | null); }[] = [];

    if (isFacebookBrowser) {
      options.push({
        message: _dict.fbBrowserUnsupported[lang],
      });
    }else{
        options.push({
            message: _dict.inAppBrowserUnsupported[lang],
        });
    }
    if (isAndroid) {
      options.push({
        message: _dict.downloadApp[lang],
        action: () => window.open('https://play.google.com/store/apps/details?id=app.web.dsapp_31794.twapp', '_blank')
      });
    }
    options.push({
      message: _dict.copyLinkBrowser[lang],
      action: copyToClipboard
    });

    setState(prevState => ({
      ...prevState,
      options,
    }));
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => setState(prevState => ({ ...prevState, showAlert: true })))
      .catch(err => console.error('Failed to copy: ', err));
  };

  const nextOption = () => {
    setState(prevState => ({
      ...prevState,
      currentOptionIndex: prevState.currentOptionIndex + 1 < prevState.options.length ? prevState.currentOptionIndex + 1 : 0, // Cycle through options
    }));
  };

  const currentOption = state.options[state.currentOptionIndex];
  if (!currentOption) return null;

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '24px', backgroundColor: '#FFFFFF', textAlign: 'center',
          borderRadius: '12px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)', border: '1px solid #E0E0E0',
          transition: 'box-shadow 0.3s ease'
        }}>
          <div>{currentOption.message}</div>
          {state.currentOptionIndex === state.options.length - 1 && (
            <IonInput value={window.location.href}
              readonly
              style={{ textAlign: 'center', fontSize: '16px', backgroundColor: '#F5F5F5', width: '100%', maxWidth: '360px', borderRadius: "8px", padding: '14px', border: '1px solid #D0D0D0', marginTop: '20px', transition: 'background-color 0.3s ease' }}
            />
          )}
          {currentOption.action && (
            <IonButton expand="block"
              onClick={currentOption.action}
              style={{ "--background": _defaultButtonColor, width: 'fit-content', padding: '10px 36px', borderRadius: '8px', marginTop: '20px', fontSize: '16px' }}
            >
              <IonIcon icon={copyOutline} slot="start" />
              {state.currentOptionIndex === state.options.length - 1 ? _dict.copyLinkButton[lang] : _dict.openInPlayStore[lang]}
            </IonButton>
          )}
          {state.options.length > 1 && (
            <IonButton fill="clear"
              onClick={nextOption}
              style={{ marginTop: '20px', fontSize: '16px' }}
            >
              {_dict.anotherSolution[lang]}
            </IonButton>
          )}
        </div>
      </IonContent>

      <IonAlert
        isOpen={state.showAlert}
        onDidDismiss={() => setState(prevState => ({ ...prevState, showAlert: false }))}
        header={_dict.urlCopied[lang]}
        message={_dict.pasteInBrowser[lang]}
        buttons={['OK']}
      />
    </IonPage>
  );
};

export default BrowserWarning;
